import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Search, Tag, Folder, FolderOpen, 
  BarChart3, ArrowUpRight, ArrowDownRight, Minus,
  Loader2, Info, ChevronDown, ChevronUp
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Mini gráfico de barras
const MiniBarChart = ({ data, maxValue }) => {
  if (!data || data.length === 0) return null;
  
  const max = maxValue || Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end gap-0.5 h-8">
      {data.slice(-14).map((d, i) => (
        <div
          key={i}
          className="w-1.5 bg-[var(--primary)] rounded-t opacity-70 hover:opacity-100 transition-opacity"
          style={{ height: `${Math.max((d.count / max) * 100, 5)}%` }}
          title={`${d.date}: ${d.count} buscas`}
        />
      ))}
    </div>
  );
};

// Indicador de tendência
const TrendIndicator = ({ current, previous }) => {
  if (previous === 0) {
    return current > 0 ? (
      <span className="text-green-600 text-xs flex items-center gap-0.5">
        <ArrowUpRight className="w-3 h-3" />
        Novo
      </span>
    ) : null;
  }
  
  const change = ((current - previous) / previous) * 100;
  
  if (change > 0) {
    return (
      <span className="text-green-600 text-xs flex items-center gap-0.5">
        <ArrowUpRight className="w-3 h-3" />
        +{change.toFixed(0)}%
      </span>
    );
  } else if (change < 0) {
    return (
      <span className="text-red-500 text-xs flex items-center gap-0.5">
        <ArrowDownRight className="w-3 h-3" />
        {change.toFixed(0)}%
      </span>
    );
  }
  
  return (
    <span className="text-gray-400 text-xs flex items-center gap-0.5">
      <Minus className="w-3 h-3" />
      0%
    </span>
  );
};

// Componente de métrica de comparação
const ComparisonMetric = ({ label, myValue, avgValue, percent, status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'above': return 'text-green-600';
      case 'below': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'above': return <ArrowUpRight className="w-4 h-4" />;
      case 'below': return <ArrowDownRight className="w-4 h-4" />;
      default: return <Minus className="w-4 h-4" />;
    }
  };
  
  const getBarWidth = () => {
    if (avgValue === 0) return myValue > 0 ? 100 : 0;
    const ratio = (myValue / avgValue) * 50; // 50% = média
    return Math.min(Math.max(ratio, 5), 100);
  };

  return (
    <div className="flex items-center gap-3">
      <div className="w-28 text-xs text-gray-600">{label}</div>
      <div className="flex-1">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              status === 'above' ? 'bg-green-500' : status === 'below' ? 'bg-red-400' : 'bg-gray-400'
            }`}
            style={{ width: `${getBarWidth()}%` }}
          />
        </div>
      </div>
      <div className="flex items-center gap-1 w-20 justify-end">
        <span className="text-xs font-semibold">{myValue}</span>
        <span className="text-xs text-gray-400">/ {avgValue}</span>
      </div>
      <div className={`flex items-center gap-0.5 w-16 justify-end ${getStatusColor(status)}`}>
        {getStatusIcon(status)}
        <span className="text-xs font-medium">
          {percent > 0 ? '+' : ''}{percent}%
        </span>
      </div>
    </div>
  );
};

export default function SearchTrendsWidget({ businessId }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    if (businessId && token) {
      fetchSearchTrends();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, token, period]);

  const fetchSearchTrends = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/analytics/search-trends/${businessId}`, {
        params: { days: period }
      });
      setData(res.data);
    } catch (err) {
      console.error('Erro ao carregar tendências:', err);
      setError('Não foi possível carregar as tendências de busca');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--primary)]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border p-4">
        <div className="text-center py-4 text-gray-500">
          <Info className="w-6 h-6 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const totalSearches = data.summary?.total_categoria_searches + 
                        data.summary?.total_subcategoria_searches + 
                        data.summary?.total_tags_searches;

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="search-trends-widget">
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Tendências de Busca</h3>
            <p className="text-sm text-gray-500">
              {totalSearches} buscas nos últimos {period} dias
            </p>
          </div>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(Number(e.target.value))}
          className="text-sm border rounded-lg px-2 py-1 bg-white"
        >
          <option value={7}>7 dias</option>
          <option value={30}>30 dias</option>
          <option value={90}>90 dias</option>
        </select>
      </div>

      {/* Cards de resumo - Sempre visíveis */}
      <div className="px-4 py-3 grid grid-cols-3 gap-3">
        <div className="bg-purple-50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Folder className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-600">Categoria</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{data.categoria?.total || 0}</p>
          <p className="text-xs text-gray-500 truncate">{data.categoria?.name || 'N/A'}</p>
        </div>

        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <FolderOpen className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-600">Subcategoria</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{data.subcategoria?.total || 0}</p>
          <p className="text-xs text-gray-500 truncate">{data.subcategoria?.name || 'N/A'}</p>
        </div>

        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Tag className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-green-600">Tags</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{data.summary?.total_tags_searches || 0}</p>
          <p className="text-xs text-gray-500">{data.tags?.length || 0} tags</p>
        </div>
      </div>

      {/* Comparação com a Categoria - SEMPRE VISÍVEL */}
      {data.category_comparison && (
        <div className="px-4 pb-4">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              <h4 className="font-semibold text-gray-900">Comparação com a Categoria</h4>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Seu negócio vs. média de {data.category_comparison.total_businesses_in_category} negócios em "{data.category_comparison.category_name}"
            </p>
            
            <div className="space-y-2.5">
              <ComparisonMetric
                label="Visualizações"
                myValue={data.category_comparison.metrics.views.my_value}
                avgValue={data.category_comparison.metrics.views.category_avg}
                percent={data.category_comparison.metrics.views.comparison_percent}
                status={data.category_comparison.metrics.views.status}
              />
              <ComparisonMetric
                label="Cliques WhatsApp"
                myValue={data.category_comparison.metrics.whatsapp_clicks.my_value}
                avgValue={data.category_comparison.metrics.whatsapp_clicks.category_avg}
                percent={data.category_comparison.metrics.whatsapp_clicks.comparison_percent}
                status={data.category_comparison.metrics.whatsapp_clicks.status}
              />
              <ComparisonMetric
                label="Favoritos"
                myValue={data.category_comparison.metrics.favorites.my_value}
                avgValue={data.category_comparison.metrics.favorites.category_avg}
                percent={data.category_comparison.metrics.favorites.comparison_percent}
                status={data.category_comparison.metrics.favorites.status}
              />
              <ComparisonMetric
                label="Compartilhamentos"
                myValue={data.category_comparison.metrics.shares.my_value}
                avgValue={data.category_comparison.metrics.shares.category_avg}
                percent={data.category_comparison.metrics.shares.comparison_percent}
                status={data.category_comparison.metrics.shares.status}
              />
              <ComparisonMetric
                label="Avaliações"
                myValue={data.category_comparison.metrics.reviews.my_value}
                avgValue={data.category_comparison.metrics.reviews.category_avg}
                percent={data.category_comparison.metrics.reviews.comparison_percent}
                status={data.category_comparison.metrics.reviews.status}
              />
            </div>
          </div>
        </div>
      )}

      {/* Detalhes adicionais - Acordeon opcional */}
      {(data.top_search_terms?.length > 0 || data.tags?.length > 0) && (
        <div className="border-t">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full px-4 py-3 flex items-center justify-between text-sm text-gray-600 hover:bg-gray-50"
          >
            <span>Ver mais detalhes (termos buscados, tags)</span>
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {showDetails && (
            <div className="px-4 pb-4 space-y-4">
              {/* Gráfico de Categoria */}
              {data.categoria && data.categoria.daily?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Buscas por "{data.categoria.name}"
                    </span>
                    <span className="text-sm text-gray-500">{data.categoria.total} total</span>
                  </div>
                  <MiniBarChart data={data.categoria.daily} />
                </div>
              )}

              {/* Tags mais buscadas */}
              {data.tags && data.tags.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Tags mais buscadas</p>
                  <div className="space-y-2">
                    {data.tags.slice(0, 5).map((tag, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 bg-gray-100 rounded text-xs flex items-center justify-center font-medium">
                            {i + 1}
                          </span>
                          <span className="text-sm text-gray-700">{tag.name}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{tag.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Termos de busca populares */}
              {data.top_search_terms && data.top_search_terms.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    <Search className="w-4 h-4 inline mr-1" />
                    Termos mais buscados na categoria
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {data.top_search_terms.slice(0, 8).map((term, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700"
                      >
                        {term.term} ({term.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Dica */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Dica:</strong> Adicione mais tags relevantes ao seu negócio para aparecer em mais buscas.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
